import {CurrentUser, User, UserData, UserInsights} from "types/User";
import client from "services/ngage/api-client";
import {queryString} from "services/string";
import deserializeUser from "./deserializers/user";
import deserializeUserInsights from "./deserializers/user-insights";
import {withoutEmpty, snakeKeys} from "services/array";
import DataResource from "types/DataResource";

type UserFilters = {
    search?: string | null
    roles?: string | null
    sort?: string | null
    page?: number
    perPage?: number
}

/**
 * Filter the given data object to remove any keys with empty values
 *
 * @internal
 * @param data
 */
function filterData(data: UserData) {
    return Object.fromEntries(Object.entries(data).filter(([,value]) => typeof value !== 'string' || value !== ''))
}

/**
 * Get the currently logged-in user
 */
const current = async () => {
    const response = await client.get<CurrentUser | null>('/user')

    return response.data
}

/**
 * Get all users (paginated)
 */
const all = async (page: number = 1, perPage: number = 100) => {
    const query = queryString({page, per_page: perPage})
    const response = await client.get<DataResource<User> | null>(`/users?${query}`)

    if (response.data) {
        response.data.data = response.data.data.map(user => deserializeUser(user))
    }

    return response.data
}

/**
 * Get all users that match the given filters (paginated)
 */
const filtered = async (filters: UserFilters = {}) => {
    const params = Object.assign({page: 1, per_page: 100}, snakeKeys(withoutEmpty(filters)))
    const response = await client.get<DataResource<User> | null>('/users', {params})

    if (response.data) {
        response.data.data = response.data.data.map(user => deserializeUser(user))
    }

    return response.data
}

/**
 * Get a single user
 *
 * @param {number} id
 */
const get = async (id: number) => {
    const response = await client.get<User>(`/users/${id}`)
    return response.data ? deserializeUser(response.data) : null
}

/**
 * Store a new user
 *
 * @param data
 */
const store = async (data: UserData) => {
    const response = await client.post<User>('/users', data)
    return response.data ? deserializeUser(response.data) : null
}

/**
 * Update an existing user
 *
 * @param {number} id
 * @param data
 */
const update = async (id: number, data: UserData) => {
    const response = await client.put<User>(`/users/${id}`, filterData(data))
    return response.data ? deserializeUser(response.data) : null
}

/**
 * Permanently delete a user
 *
 * @param {number} id
 */
const destroy = async (id: number) => {
    const response = await client.delete<null>(`/users/${id}`)
    return response.status === 204
}

/**
 * Get a single user's insights
 *
 * @param {number} id
 */
const insights = async (id: number) => {
    const response = await client.get<UserInsights>(`/users/${id}/insights`)
    return response.data ? deserializeUserInsights(response.data) : null
}

const users = {
    current,
    all,
    filtered,
    get,
    store,
    update,
    destroy,
    insights,
}

export default users;